<template>
  <div>
    <ThemeDefaultMenuBreadcrumb :items="breadcrumbItem" />
    <ThemeDefaultContainerPage
      v-show="product"
      custom-padding="xl:tw-py-11 tw-pt-4 tw-pb-8 lg:tw-pb-8"
      class="product-detail-content"
    >
      <div
        class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[363px_1fr] tw-gap-5 sm:tw-gap-8 tw-mb-5"
      >
        <div class="tw-relative tw-overflow-hidden">
          <BadgeRecommended v-show="product.recommended" />
          <MediaImageLazyLoad
            :src="product?.imageCover?.large || ''"
            :placeholder-src="product?.imageCover?.tiny || ''"
            :alt="product.title"
            width="100%"
            custom-class="tw-rounded-lg"
            seo-optimize
          />
        </div>
        <div>
          <div data-allow-mismatch class="tw-flex tw-flex-col tw-h-full">
            <div class="tw-text-mb-body-small lg:tw-text-dt-body-small tw-mb-1">
              <NuxtLink
                :to="
                  getPath({
                    componentName: 'Product',
                    categorySlug: product.productCategory?.slug,
                  })
                "
                :title="product.productCategory?.title"
                class="product-category-link link-underline-hover"
              >
                {{ product.productCategory?.title }}
              </NuxtLink>
            </div>
            <h1
              class="tw-text-mb-title-1 lg:tw-text-dt-title-1 tw-text-black tw-mb-3"
            >
              {{ product.title }}
            </h1>
            <DisplayHTMLContent
              class="html-content product-description tw-mb-5"
              :model-value="product.description || ''"
            />
            <DisplayPrice
              data-allow-mismatch
              :options="product.options || []"
              :show-options="product.showProductOptions"
              :min-price-option="product.minPriceOption"
              :max-price-option="product.maxPriceOption"
              :min-price-of-discounted-option="
                product.minPriceOfDiscountedOption
              "
              :max-price-of-discounted-option="
                product.maxPriceOfDiscountedOption
              "
              :price="product.price"
              :price-of-discounted="product.priceOfDiscounted"
              single-product-discount-container-class="tw-flex-row-reverse tw-gap-x-2 tw-items-center"
              discounted-price-range-container-class="tw-w-full tw-flex-row-reverse tw-flex-wrap tw-justify-end tw-gap-x-3 tw-items-center"
              class="tw-mb-5"
              :show-border-option-name="false"
              :show-margin-between-options="false"
              options-container-class="tw-grid tw-grid-cols-2 tw-mb-3 last:tw-mb-5"
              discounted-class="tw-line-through tw-text-gray lg:tw-text-dt-body tw-text-mb-body-small tw-justify-end tw-items-end"
              option-price-class="tw-text-mb-title-5 lg:tw-text-dt-title-5 tw-text-black tw-justify-end tw-items-end"
              option-name-class="tw-text-mb-body-small lg:tw-text-dt-body"
              option-price-container-class="tw-text-right"
              option-discounted-container-class="tw-flex-row-reverse tw-items-center tw-gap-2"
              regular-option-price-class="tw-text-mb-title-5 lg:tw-text-dt-title-5"
              :main-price-class="[
                'tw-text-mb-title-3',
                'lg:tw-text-dt-title-3',
                'tw-relative',
                'tw--top-0.5',
                isMobile ? 'tw-flex tw-flex-wrap' : '',
              ]"
              custom-horizontal-line="!tw-mb-5"
            />
            <div ref="mobileCtaRef" data-allow-mismatch>
              <ThemeDefaultButtonCallToAction
                :enable-button="{
                  facebook: product.enableButton.facebook,
                  line: product.enableButton.line,
                  whatsapp: product.enableButton.whatsapp,
                  tel: product.enableButton.tel,
                  callToAction: product.enableButton.callToAction,
                  callToActionTitle: product.callToActionTitle,
                  callToActionIcon: product.callToActionIcon,
                  callToActionLink: product.callToActionLink,
                }"
                :category-event-name="categoryEventName"
                :event-name="eventButtonName"
                button-class="!tw-h-12"
                footer-class="tw-h-20 tw-px-4 tw-py-4 tw-items-center"
                :hide-social-title="isMobile"
                :bottom-sheet-card-style="{ borderRadius: '20px 20px 0 0' }"
                bottom-sheet-header-class="!tw-mb-5"
                bottom-sheet-grid-class="!tw-gap-3"
                class="tw-mb-4"
                :show-footer="!isCtaVisible"
              />
            </div>

            <ThemeDefaultButtonShare
              :category-event-name="categoryEventName"
              :title-name="product.title"
              :event-name="eventShareName"
            />
          </div>
        </div>
      </div>
      <HorizontalLine v-if="product.content" custom-class="tw-mb-5" />
      <div v-if="product.content">
        <DisplayHTMLContent
          class="tw-text-dark-gray"
          :model-value="product.content"
        />
      </div>
      <div v-show="relateProducts && relateProducts?.length > 0">
        <HorizontalLine custom-class="tw-mt-2 tw-mb-5" />
        <h2 class="tw-text-mb-title-2 lg:tw-text-dt-title-2 tw-mb-5">
          {{ $t("global.relateOf", { title: menu?.title }) }}
        </h2>
        <div class="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6">
          <div
            v-for="item in relateProducts?.slice(0, 4)"
            :key="`relate-product-${item._id}`"
            class="tw-col-span-1"
          >
            <ThemeDefaultCardProduct
              title-tag="h3"
              :image="item.imageCover"
              :recommended="item.recommended"
              :title="item.title"
              :path="getPath({ componentName: 'Product', slug: item.slug })"
              :category="{
                _id: item.productCategory._id,
                slug: item.productCategory.slug,
                title: item.productCategory.title,
                path: getPath({
                  componentName: 'Product',
                  categorySlug: item.productCategory.slug,
                }),
              }"
              :options="item.options"
              :show-option-list="false"
              :price="item.price || 0"
              :price-of-discounted="item.priceOfDiscounted || 0"
              :max-price-option="item.maxPriceOption || 0"
              :min-price-option="item.minPriceOption || 0"
              :min-price-of-discounted-option="
                item.minPriceOfDiscountedOption || 0
              "
              :max-price-of-discounted-option="
                item.maxPriceOfDiscountedOption || 0
              "
              @click="eventProductRelated(item.title)"
            />
          </div>
        </div>
      </div>
    </ThemeDefaultContainerPage>
  </div>
</template>

<script lang="ts" setup>
import { buildFaqPageSchema, dedupeFaqItems, extractFaqsFromHtml } from '~/composables/utils/faq'

const {
  menu,
  product,
  relateProducts,
  breadcrumbItem,
  getPath,
  eventProductRelated,
  categoryEventName,
  eventShareName,
  eventButtonName,
} = await useDetailProductPage();
const { isMobile } = useDevice();
const faqItems = computed(() => dedupeFaqItems(extractFaqsFromHtml(product.value?.content || '')))
useHead(() => faqItems.value.length ? {
  script: [{ type: 'application/ld+json', children: JSON.stringify(buildFaqPageSchema(faqItems.value)) }],
} : {})

const mobileCtaRef = ref<HTMLElement | null>(null);
const isCtaVisible = ref(true);

onMounted(() => {
  if (!mobileCtaRef.value) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isCtaVisible.value = entry.isIntersecting;
      });
    },
    {
      threshold: 0.1,
    },
  );

  observer.observe(mobileCtaRef.value as unknown as Element);

  onUnmounted(() => {
    observer.disconnect();
  });
});
</script>

<style scoped>
.product-category-link {
  color: var(--theme-color-dark-gray) !important;
  text-decoration: none !important;
}

.product-category-link:hover {
  color: var(--theme-color-primary) !important;
  text-decoration: none !important;
}

.product-category-link::after {
  display: none !important;
}

:deep(.product-description p) {
  color: #000 !important;
}

:deep(.product-description a) {
  color: var(--theme-color-primary) !important;
}

@media (min-width: 1024px) {
  :deep(.html-content) {
    font-size: var(--theme-font-text-dt-link-size);
    line-height: var(--theme-font-text-dt-link-line-height);
    font-weight: var(--theme-font-text-dt-link-weight);
  }
}

@media (max-width: 1023px) {
  :deep(.html-content) {
    font-size: var(--theme-font-text-mb-link-size);
    line-height: var(--theme-font-text-mb-link-line-height);
    font-weight: var(--theme-font-text-mb-link-weight);
  }
}
</style>

<style>
@media (min-width: 1280px) {
  .product-detail-content {
    max-width: 996px !important;
    padding-left: 3rem !important;
    padding-right: 3rem !important;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
